import { Component, Input, forwardRef, Optional, Self, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'ui-file-upload',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <div class="flex flex-col gap-1">
      <label *ngIf="label" [for]="id" class="text-sm font-medium text-slate-700">
        {{ label }}
        <span *ngIf="required" class="text-danger-500">*</span>
      </label>

      <div class="relative">
        <div
          class="border-2 border-dashed border-gray-200 rounded-lg p-8"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)"
          [class.border-primary-500]="isDragging()"
          [class.bg-primary-50]="isDragging()"
        >
          <div class="flex flex-col items-center justify-center text-center space-y-4">
            <ui-icon [name]="isDragging() ? 'document-plus' : 'document-upload'"
                    class="w-12 h-12"
                    [class.text-primary-500]="isDragging()"
                    [class.text-gray-400]="!isDragging()"
            ></ui-icon>
            <div class="space-y-1">
              <p class="text-base text-gray-600">
                Sleep bestanden hierheen
              </p>
              <p class="text-sm text-gray-500">of</p>
              <button
                type="button"
                class="px-4 py-2 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                (click)="fileInput.click()"
                [disabled]="disabled"
              >
                Selecteer bestand
              </button>
            </div>
          </div>

          <input
            #fileInput
            type="file"
            [id]="id"
            class="hidden"
            [accept]="accept"
            [multiple]="multiple"
            [disabled]="disabled"
            (change)="onFileSelected($event)"
          />
        </div>

        <!-- Selected Files List -->
        <div *ngIf="value?.length" class="mt-4 space-y-2">
          <div *ngFor="let file of value; let i = index"
               class="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div class="flex items-center gap-2">
              <ui-icon name="document-text" class="w-5 h-5 text-gray-500"></ui-icon>
              <span class="text-sm">{{ file.name }}</span>
            </div>
            <button
              type="button"
              (click)="removeFile(i)"
              class="text-danger-500 hover:text-danger-600"
              [disabled]="disabled"
            >
              <ui-icon name="delete" class="w-5 h-5"></ui-icon>
            </button>
          </div>
        </div>

        <div *ngIf="shouldShowError()" class="mt-1 text-sm text-danger-600">
          {{ getErrorMessage() }}
        </div>

        <p class="mt-2 text-sm text-gray-500">
          {{ helperText }}
        </p>
      </div>
    </div>
  `
})
export class FileUploadComponent implements ControlValueAccessor {
  @Input() id: string = '';
  @Input() label?: string;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() error?: string;
  @Input() multiple: boolean = false;
  @Input() accept: string = '.pdf,.doc,.docx';
  @Input() maxFileSize: number = 10 * 1024 * 1024; // 10MB default
  @Input() helperText: string = 'Toegestane bestandstypen: PDF, DOC, DOCX (max 10MB)';

  value: File[] = [];
  isDragging = signal<boolean>(false);

  private onChange = (value: File[]) => {};
  private onTouched = () => {};

  constructor(@Optional() @Self() private ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const newFiles = Array.from(input.files);
      if (this.multiple) {
        const currentFiles = this.value || [];
        const updatedFiles = [...currentFiles, ...newFiles];
        this.onChange(updatedFiles);
        this.writeValue(updatedFiles);
      } else {
        this.onChange(newFiles);
        this.writeValue(newFiles);
      }
    }
  }

  removeFile(index: number): void {
    const newFiles = this.value.filter((_, i) => i !== index);
    this.value = newFiles;
    this.onChange(newFiles);
  }

  private handleFiles(files: File[]): void {
    const validFiles = files.filter(file => {
      // Check file size
      if (file.size > this.maxFileSize) {
        this.setError(`Bestand ${file.name} is te groot. Maximum is ${this.maxFileSize / 1024 / 1024}MB`);
        return false;
      }

      // Check file type
      if (!this.isValidFileType(file)) {
        this.setError(`Bestandstype ${file.type} is niet toegestaan`);
        return false;
      }

      return true;
    });

    if (!this.multiple) {
      this.value = validFiles.slice(0, 1);
    } else {
      this.value = [...this.value, ...validFiles];
    }

    this.onChange(this.value);
    this.onTouched();
  }

  private isValidFileType(file: File): boolean {
    const acceptedTypes = this.accept.split(',').map(type => type.trim());
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    return acceptedTypes.includes(fileExtension);
  }

  private setError(message: string): void {
    if (this.ngControl) {
      this.ngControl.control?.setErrors({ custom: message });
    }
  }

  shouldShowError(): boolean {
    if (this.error) return true;
    return !!(this.ngControl?.touched && this.ngControl?.errors);
  }

  getErrorMessage(): string | undefined {
    if (this.error) return this.error;

    if (this.ngControl?.errors) {
      if (this.ngControl.errors['required']) {
        return 'Een bestand is verplicht';
      }
      if (this.ngControl.errors['custom']) {
        return this.ngControl.errors['custom'];
      }
    }
    return undefined;
  }

  writeValue(files: File[]): void {
    this.value = files || [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
